import { Station, ProjectConfig } from '../types';

interface PopulationData {
  population: number;
  radius: number;
}

interface RetryConfig {
  maxAttempts: number;
  initialDelay: number;
  maxDelay: number;
  backoffFactor: number;
}

async function withSilentRetry<T>(
  operation: () => Promise<T>,
  config: RetryConfig = {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffFactor: 2
  }
): Promise<T> {
  let lastError: Error | null = null;
  let delay = config.initialDelay;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      if (attempt === config.maxAttempts) {
        console.log(`[Population] All retry attempts failed for operation`);
        throw lastError;
      }
      console.log(`[Population] Retry attempt ${attempt}/${config.maxAttempts} after ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * config.backoffFactor, config.maxDelay);
    }
  }

  throw lastError;
}

export async function fetchPopulationData(lat: number, lng: number, config: ProjectConfig): Promise<PopulationData> {
  const radius = config.calculation.populationRadius;
  console.log(`[Population] Fetching new circle population for lat: ${lat}, lng: ${lng}, radius: ${radius}km`);
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = isLocalhost 
    ? 'https://cors-anywhere.herokuapp.com/https://ringpopulationsapi.azurewebsites.net/api/globalringpopulations'
    : 'https://ringpopulationsapi.azurewebsites.net/api/globalringpopulations';
  
  return withSilentRetry(async () => {
    try {
      const response = await fetch(`${baseUrl}?latitude=${lat}&longitude=${lng}&distance_km=${radius}`);
      if (!response.ok) {
        if (response.status === 403 && isLocalhost) {
          console.error(`[Population] CORS proxy access denied. Please visit https://cors-anywhere.herokuapp.com/corsdemo to enable the demo server.`);
          throw new Error('CORS proxy access denied. Please visit https://cors-anywhere.herokuapp.com/corsdemo to enable the demo server.');
        }
        console.error(`[Population] API call failed with status: ${response.status}`);
        throw new Error('Failed to fetch population data');
      }
      
      const text = await response.text();
      console.log(`[Population] Raw API response:`, text);
      
      try {
        const data = JSON.parse(text);
        console.log(`[Population] Received new circle population:`, {
          population: data[0].people,
          radius: radius,
          requestedRadius: radius
        });
        return {
          population: data[0].people,
          radius: radius
        };
      } catch (e) {
        if (isLocalhost && text.includes('cors-anywhere.herokuapp.com')) {
          console.error(`[Population] CORS proxy not enabled. Please visit https://cors-anywhere.herokuapp.com/corsdemo first to enable the demo server.`);
          throw new Error('CORS proxy not enabled. Please visit https://cors-anywhere.herokuapp.com/corsdemo first.');
        }
        throw new Error('Invalid response from API');
      }
    } catch (error) {
      console.error(`[Population] API call failed:`, error);
      throw error;
    }
  });
}

export function getEffectivePopulation(station: Station): number {
  return station.population_source === 'automatic' 
    ? (station.circle_population ?? station.population)
    : station.population;
}

export async function updateStationPopulation(station: Station, config: ProjectConfig): Promise<Station> {
  if (!station.latitude || !station.longitude) {
    throw new Error('Station is missing coordinates');
  }

  const data = await fetchPopulationData(station.latitude, station.longitude, config);
  const updatedStation = {
    ...station,
    circle_population: data.population,
    circle_population_radius: data.radius
  };
  console.log(`[Population] Saved new circle population for ${station.name}:`, {
    population: data.population,
    radius: data.radius,
    requestedRadius: config.calculation.populationRadius
  });
  return updatedStation;
} 